import { useTranslate } from "@tolgee/react";
import { useMemo, useState } from "react";
import { KrwSubtext, useIsKoreanLocale } from "#/components/krw-subtext";
import { Button } from "#/components/ui/button";
import { toNumber } from "#/lib/formatters";
import { cn } from "#/lib/utils";
import { useBestBidAsk } from "../../hooks/use-best-bid-ask";
import { getApiCoin } from "../../lib/hyperliquid";
import { QuickPriceButton } from "../order-form/quick-price-button";
import { sanitizeNumericInput } from "../order-form/utils/sanitize-numeric-input";

const PRICE_MAX_DECIMALS = 8;

const fieldClass =
	"flex items-center gap-1.5 rounded-[8px] border border-trade-border-subtle bg-trade-surface-inset px-2.5 py-1.5 sm:gap-2 sm:rounded-[10px] sm:px-3.5 sm:py-2";

const inputClass =
	"w-full bg-transparent text-label1-bold text-trade-text outline-none placeholder:text-trade-text-faint sm:text-body2-bold";

const labelClass = "whitespace-nowrap text-label2 sm:text-body2";

type PositionLimitCloseFormProps = {
	coin: string;
	absSize: string;
	isLong: boolean;
	onSubmit: (
		coin: string,
		size: string,
		isLong: boolean,
		price: string,
	) => void;
	onClose: () => void;
};

export function PositionLimitCloseForm({
	coin,
	absSize,
	isLong,
	onSubmit,
	onClose,
}: PositionLimitCloseFormProps) {
	const { t } = useTranslate();
	const isKorean = useIsKoreanLocale();
	const [limitPrice, setLimitPrice] = useState("");

	const apiCoin = useMemo(() => getApiCoin(coin), [coin]);
	const { bestBid, bestAsk } = useBestBidAsk(apiCoin);

	const priceNumeric = toNumber(limitPrice);
	const showPriceKrw = isKorean && priceNumeric > 0;

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				if (!limitPrice) return;
				onSubmit(coin, absSize, isLong, limitPrice);
				onClose();
				setLimitPrice("");
			}}
		>
			<label className={cn(fieldClass, "mb-2")}>
				<div className="flex w-full items-center gap-2 sm:gap-3">
					<p className={labelClass}>{t("positions.limit-price")}</p>
					<div className="flex-1">
						<input
							type="text"
							inputMode="decimal"
							className={cn(inputClass, "text-right")}
							value={limitPrice}
							onChange={(e) =>
								setLimitPrice(
									sanitizeNumericInput(e.target.value, PRICE_MAX_DECIMALS),
								)
							}
							placeholder="0"
							autoFocus
						/>
						{showPriceKrw && (
							<div className="flex justify-end">
								<KrwSubtext
									usd={priceNumeric}
									className="text-caption1 sm:text-label2"
								/>
							</div>
						)}
					</div>
				</div>
			</label>
			<div className="mb-2 flex gap-1.5">
				<QuickPriceButton
					side="buy"
					price={bestBid}
					showPrice
					compactOnMobile={false}
					onApplyPrice={setLimitPrice}
					className="flex-1"
				/>
				<QuickPriceButton
					side="sell"
					price={bestAsk}
					showPrice
					compactOnMobile={false}
					onApplyPrice={setLimitPrice}
					className="flex-1"
				/>
			</div>
			<Button
				type="submit"
				size="sm"
				disabled={!limitPrice}
				className="w-full text-[var(--color-theme-static-white)]"
			>
				{isLong ? t("positions.close-long") : t("positions.close-short")}
			</Button>
		</form>
	);
}
